import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    Timestamp,
    updateDoc,
    where
} from "firebase/firestore";
import { ref, deleteObject, listAll } from "firebase/storage";
import { db, storage } from "../firebase";
import { isSubscriptionAccessAllowed } from "../utils/subscriptionAccess";
import { getInventoryCatalog, getInventoryItem, listInventoryItems } from "./inventoryService";
import { sendOfferMessage } from "./whatsappService";

// Helper function to extract contact information from dynamic form data
const extractContactInfo = (formData) => {
    const contactInfo = {
        name: null,
        email: null,
        phone: null,
        company: null
    };

    // Common field name variations for contact information
    const nameFields = ['fullName', 'full_name', 'firstName', 'first_name', 'contactName', 'contact_name', 'customerName', 'customer_name', 'clientName', 'client_name', 'name'];
    const emailFields = ['email', 'emailAddress', 'email_address', 'contactEmail', 'contact_email', 'customerEmail', 'clientEmail'];
    const phoneFields = ['phone', 'phoneNumber', 'phone_number', 'mobile', 'telephone', 'contactPhone', 'contact_phone', 'customerPhone', 'clientPhone'];
    const companyFields = ['company', 'companyName', 'company_name', 'business', 'organization', 'businessName', 'clientCompany'];
    
    // Fields to exclude (product/service related)
    const excludeFields = ['product', 'service', 'item', 'inventory', 'price', 'cost', 'amount', 'quantity', 'description', 'details', 'message', 'comment', 'notes'];

    // Extract information by checking common field variations
    Object.keys(formData).forEach(key => {
        const lowerKey = key.toLowerCase();
        const value = formData[key];

        // Skip if this is a product/service field
        if (excludeFields.some(excludeField => lowerKey.includes(excludeField))) {
            return;
        }

        // Check for name fields
        if (!contactInfo.name) {
            const nameField = nameFields.find(field => 
                lowerKey === field.toLowerCase() || 
                lowerKey.includes(field.toLowerCase())
            );
            if (nameField && value && typeof value === 'string' && value.trim()) {
                contactInfo.name = value.trim();
            }
        }
        
        // Check for email fields
        if (!contactInfo.email) {
            const emailField = emailFields.find(field => 
                lowerKey === field.toLowerCase() || 
                lowerKey.includes(field.toLowerCase())
            );
            if (emailField && value && typeof value === 'string' && value.includes('@')) {
                contactInfo.email = value.trim();
            }
        }
        
        // Check for phone fields
        if (!contactInfo.phone) {
            const phoneField = phoneFields.find(field => 
                lowerKey === field.toLowerCase() || 
                lowerKey.includes(field.toLowerCase())
            );
            if (phoneField && value && typeof value === 'string' && value.trim()) {
                contactInfo.phone = value.trim();
            }
        }
        
        // Check for company fields
        if (!contactInfo.company) {
            const companyField = companyFields.find(field => 
                lowerKey === field.toLowerCase() || 
                lowerKey.includes(field.toLowerCase())
            );
            if (companyField && value && typeof value === 'string' && value.trim()) {
                contactInfo.company = value.trim();
            }
        }
    });

    return contactInfo;
};

// Helper function to generate a display name from contact info
const generateDisplayName = (contactInfo) => {
    if (contactInfo.name) {
        return contactInfo.name;
    } else if (contactInfo.email) {
        return contactInfo.email;
    } else if (contactInfo.company) {
        return `${contactInfo.company} Lead`;
    } else {
        return "Unnamed Lead";
    }
};

// Helper function to extract product information from form data
export const extractProductInfo = (formData) => {
    const productInfo = {
        category: null,
        productName: null,
        condition: null,
        variant: null
    };

    // First, check for exact field names that are commonly used in forms
    // These take priority over fuzzy matching
    if (formData["Primary Variant"] && typeof formData["Primary Variant"] === 'string' && formData["Primary Variant"].trim()) {
        productInfo.variant = formData["Primary Variant"].trim();
    }
    if (formData["Condition"] && typeof formData["Condition"] === 'string' && formData["Condition"].trim()) {
        productInfo.condition = formData["Condition"].trim();
    }
    if (formData["Product Name"] && typeof formData["Product Name"] === 'string' && formData["Product Name"].trim()) {
        productInfo.productName = formData["Product Name"].trim();
    }
    if (formData["Category"] && typeof formData["Category"] === 'string' && formData["Category"].trim()) {
        productInfo.category = formData["Category"].trim();
    }

    // Common field name variations for product information
    // Normalize keys by removing spaces and special characters for matching
    const normalizeKey = (key) => key.toLowerCase().replace(/\s+/g, '').replace(/[_-]/g, '');
    
    const categoryFields = ['category', 'productcategory', 'type', 'producttype'];
    const productNameFields = ['productname', 'product', 'item', 'itemname', 'device', 'model'];
    const conditionFields = ['condition', 'productcondition', 'state', 'quality', 'grade'];
    const variantFields = ['variant', 'productvariant', 'primaryvariant', 'size', 'capacity', 'storage', 'color'];

    // Only fill in missing fields with fuzzy matching
    Object.keys(formData).forEach(key => {
        const normalizedKey = normalizeKey(key);
        const value = formData[key];

        // Check for category fields (only if not already set)
        if (!productInfo.category) {
            const categoryField = categoryFields.find(field => 
                normalizedKey === field || 
                normalizedKey.includes(field) ||
                field.includes(normalizedKey)
            );
            if (categoryField && value && typeof value === 'string' && value.trim()) {
                productInfo.category = value.trim();
            }
        }

        // Check for product name fields (only if not already set)
        if (!productInfo.productName) {
            const productNameField = productNameFields.find(field => 
                normalizedKey === field || 
                normalizedKey.includes(field) ||
                field.includes(normalizedKey)
            );
            if (productNameField && value && typeof value === 'string' && value.trim()) {
                productInfo.productName = value.trim();
            }
        }

        // Check for condition fields (only if not already set)
        if (!productInfo.condition) {
            const conditionField = conditionFields.find(field => 
                normalizedKey === field || 
                normalizedKey.includes(field) ||
                field.includes(normalizedKey)
            );
            if (conditionField && value && typeof value === 'string' && value.trim()) {
                productInfo.condition = value.trim();
            }
        }

        // Check for variant fields (only if not already set)
        if (!productInfo.variant) {
            const variantField = variantFields.find(field => 
                normalizedKey === field || 
                normalizedKey.includes(field) ||
                field.includes(normalizedKey)
            );
            if (variantField && value && typeof value === 'string' && value.trim()) {
                productInfo.variant = value.trim();
            }
        }
    });

    return productInfo;
};

// Helper function to generate offers based on inventory
export const generateOffers = async (businessId, productInfo) => {
    try {
        // Get business currency from business document
        let businessCurrency = 'ZAR'; // Default fallback
        let businessData = null;
        try {
            const businessRef = doc(db, "businesses", businessId);
            const businessDoc = await getDoc(businessRef);
            if (businessDoc.exists()) {
                businessData = businessDoc.data();
                businessCurrency = businessData.businessCurrency || businessData.currency || 'ZAR';
            }
        } catch (error) {
            // Silently use default currency if business data can't be accessed
            console.warn('Could not fetch business currency, using default:', error.message);
        }
        
        // Prefer the lightweight catalog, then load only the matching product.
        let inventoryItems = [];
        const catalog = await getInventoryCatalog(businessId);
        if (catalog) {
            const matchingSummaries = catalog.filter(item => {
                const categoryMatch = productInfo.category &&
                    item.category?.toLowerCase() === productInfo.category.toLowerCase();
                const productNameMatch = productInfo.productName &&
                    item.productName?.toLowerCase() === productInfo.productName.toLowerCase();
                return categoryMatch && productNameMatch;
            });
            const loaded = await Promise.all(
                matchingSummaries.map((item) => getInventoryItem(businessId, item.id))
            );
            inventoryItems = loaded.filter(Boolean);
        } else {
            inventoryItems = await listInventoryItems(businessId);
        }
        
        const offers = [];
        
        // Filter inventory items by category and product name first - EXACT MATCHES ONLY
        const matchingItems = inventoryItems.filter(item => {
            // Check if category matches (EXACT match only)
            const categoryMatch = productInfo.category && 
                item.category?.toLowerCase() === productInfo.category.toLowerCase();
            
            // Check if product name matches (EXACT match only)
            const productNameMatch = productInfo.productName && 
                item.productName?.toLowerCase() === productInfo.productName.toLowerCase();
            
            // Require BOTH category AND product name to match EXACTLY
            return categoryMatch && productNameMatch;
        });
        
        // Only process matching items
        for (const item of matchingItems) {
            // Check each item variant in the inventory
            for (const itemVariant of item.items || []) {
                // Check if variant matches (if provided) - use exact match (case-insensitive)
                // If variant is provided, it must match exactly
                const variantMatch = !productInfo.variant || 
                    (itemVariant.primary && 
                     itemVariant.primary.toLowerCase().trim() === productInfo.variant.toLowerCase().trim());
                
                if (variantMatch) {
                    // Check each price condition in the variant
                    for (const priceCondition of itemVariant.prices || []) {
                        // If condition is provided, match exactly (case-insensitive)
                        // Normalize both sides for comparison but require exact match
                        const normalizeCondition = (condition) => {
                            if (!condition) return '';
                            return condition.toLowerCase()
                                .replace(/\s+condition\s*$/, '') // Remove trailing "condition"
                                .replace(/\s+state\s*$/, '') // Remove trailing "state"
                                .replace(/\s+grade\s*$/, '') // Remove trailing "grade"
                                .trim();
                        };
                        
                        const normalizedFormCondition = normalizeCondition(productInfo.condition);
                        const normalizedPriceCondition = normalizeCondition(priceCondition.name);
                        
                        // If condition is provided, require exact match (after normalization)
                        // If no condition is provided, include all conditions
                        const conditionMatch = !productInfo.condition || (
                            normalizedPriceCondition === normalizedFormCondition ||
                            // Also allow exact match without normalization (in case normalization removes important parts)
                            priceCondition.name.toLowerCase().trim() === productInfo.condition.toLowerCase().trim()
                        );
                        
                        if (conditionMatch) {
                            // Create offer
                            const offer = {
                                id: `offer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                                inventoryItemId: item.id,
                                productName: item.productName,
                                category: item.category,
                                condition: priceCondition.name,
                                variant: itemVariant.primary,
                                secondaryVariant: itemVariant.secondary?.join(', ') || null,
                                price: priceCondition.price,
                                imageUrl: item.imageUrl,
                                createdAt: new Date().toISOString()
                            };
                            
                            offers.push(offer);
                        }
                    }
                }
            }
        }
        
        return offers;
    } catch (error) {
        console.error('Error generating offers:', error);
        return [];
    }
};

// Submit a new lead from a form
export const submitLead = async (businessId, formId, data) => {
    // Get form information for context (optional - don't fail if we can't access)
    let formInfo = {
        id: formId,
        title: "Form Submission",
        description: ""
    };
    
    try {
        const formRef = doc(db, "businesses", businessId, "forms", formId);
        const formDoc = await getDoc(formRef);
        if (formDoc.exists()) {
            formInfo = {
                id: formId,
                title: formDoc.data().title || "Unknown Form",
                description: formDoc.data().description || ""
            };
        }
    } catch (error) {
        // Silently handle permission errors for public form submissions
        if (error.code === 'permission-denied') {
            console.log('Using default form info for public submission');
        } else {
            console.warn("Could not fetch form info (this is okay for public submissions):", error);
        }
        // Continue with default form info
    }

    // Extract contact information
    const contactInfo = extractContactInfo(data);
    const displayName = generateDisplayName(contactInfo);
    
    // Get business currency and data for WhatsApp messages
    let businessCurrency = 'ZAR'; // Default fallback
    let businessData = null;
    try {
        const businessRef = doc(db, "businesses", businessId);
        const businessDoc = await getDoc(businessRef);
        if (businessDoc.exists()) {
            businessData = businessDoc.data();
            if (!isSubscriptionAccessAllowed(businessData)) {
                throw new Error(
                    "This form is temporarily unavailable. The business is not accepting submissions right now.",
                );
            }
            businessCurrency = businessData.businessCurrency || businessData.currency || 'ZAR';
        }
    } catch (error) {
        if (error.message && error.message.includes("temporarily unavailable")) {
            throw error;
        }
        // Silently use default currency for public form submissions
        if (error.code === 'permission-denied') {
            console.log('Using default currency for public form submission');
        } else {
            console.warn('Could not fetch business currency for WhatsApp:', error.message);
        }
    }
    
    // Extract product information and generate offers
    const productInfo = extractProductInfo(data);
    
    const offers = await generateOffers(businessId, productInfo);

    // Create the lead record with all data stored directly
    const leadsCol = collection(db, "businesses", businessId, "leads");
    const leadRef = await addDoc(leadsCol, {
        // Form information
        formId,
        formInfo,
        
        // Contact information
        displayName,
        name: contactInfo.name,
        email: contactInfo.email,
        phone: contactInfo.phone,
        company: contactInfo.company,
        
        // Original form data (all fields) - stored in stable order
        formData: data,
        formDataOrder: Object.keys(data), // Store the original order of form fields
        
        // Generated offers
        offers: offers,
        
        // Lead management
        status: "new",
        priority: "medium",
        assignedTo: null,
        assignedToName: null,
        
        // Timestamps
        submittedAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        source: "web-form",
        
        // Initial activity
        activities: [{
            type: "lead_created",
            message: `Lead created from form submission: ${formInfo.title}`,
            userId: null,
            userName: "System",
            timestamp: new Date()
        }]
    });

    // Note: Notifications are now handled by Cloud Function notifyTeamOnLead
    // which triggers automatically when a lead is created. This ensures
    // notifications work for both authenticated and unauthenticated form submissions.

    // Send WhatsApp message with offers if available
    if (offers.length > 0) {
        try {
            const leadData = {
                id: leadRef.id,
                displayName,
                phone: contactInfo.phone,
                email: contactInfo.email,
                formData: data
            };
            
            // Only attempt WhatsApp if we have proper business access
            let whatsappResults = [];
            if (businessData && businessData.whatsappConfig) {
                try {
                    whatsappResults = await sendOfferMessage(leadData, offers, businessCurrency, businessId);
                    // Log results without sensitive data
            console.log('WhatsApp results:', whatsappResults.map(result => ({
                success: result.success,
                reason: result.reason
            })));
                } catch (whatsappError) {
                    console.error('WhatsApp service error:', whatsappError);
                    whatsappResults = [{ success: false, reason: 'WhatsApp service error', error: whatsappError.message }];
                }
            } else {
                console.log('WhatsApp not available - business data not accessible');
                whatsappResults = [{ success: false, reason: 'Business data not accessible' }];
            }
            
            // Ensure whatsappResults is always an array with valid objects
            if (!Array.isArray(whatsappResults)) {
                whatsappResults = [{ success: false, reason: 'Invalid WhatsApp results format' }];
            }
            
            // Clean up any undefined values in the results
            whatsappResults = whatsappResults.map(result => ({
                success: result.success || false,
                reason: result.reason || 'Unknown error',
                phoneNumber: result.phoneNumber || null,
                messageId: result.messageId || null,
                error: result.error || null
            }));
            
            // Log WhatsApp activity
            const currentActivities = [
                {
                    type: "lead_created",
                    message: `Lead created from form submission: ${formInfo.title}`,
                    userId: null,
                    userName: "System",
                    timestamp: new Date()
                },
                {
                    type: "whatsapp_sent",
                    message: `WhatsApp offer sent to ${contactInfo.phone}`,
                    userId: null,
                    userName: "System",
                    timestamp: new Date(),
                    details: {
                        offersCount: offers.length,
                        results: whatsappResults || []
                    }
                }
            ];
            
            await updateDoc(leadRef, {
                activities: currentActivities
            });
        } catch (error) {
            console.error("Error sending WhatsApp message:", error);
            // Don't throw error - lead submission should still succeed even if WhatsApp fails
        }
    }

    return leadRef.id;
};

export const getLeadPeriodBounds = (dateFilter, selectedMonth) => {
    if (!dateFilter || dateFilter === "all") return null;

    const now = new Date();
    let startDate;
    let endDate;

    switch (dateFilter) {
        case "today":
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            break;
        case "this-week": {
            const dayOfWeek = now.getDay();
            const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            startDate = new Date(now.getFullYear(), now.getMonth(), diff);
            endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            break;
        }
        case "this-month":
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
            break;
        case "this-year":
            startDate = new Date(now.getFullYear(), 0, 1);
            endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
            break;
        case "custom-month": {
            if (!selectedMonth) return null;
            const [year, month] = selectedMonth.split("-").map(Number);
            startDate = new Date(year, month - 1, 1);
            endDate = new Date(year, month, 0, 23, 59, 59, 999);
            break;
        }
        default:
            return null;
    }

    return { startDate, endDate };
};

export const getLeadQueryBounds = (dateFilter, selectedMonth) => {
    const period = getLeadPeriodBounds(dateFilter, selectedMonth);
    if (!period) return {};

    const { startDate, endDate } = period;
    const queryStart = new Date(startDate);

    if (dateFilter === "this-month" || dateFilter === "custom-month") {
        queryStart.setMonth(queryStart.getMonth() - 1);
    } else if (dateFilter === "this-week") {
        queryStart.setDate(queryStart.getDate() - 7);
    } else if (dateFilter === "today") {
        queryStart.setDate(queryStart.getDate() - 1);
    } else if (dateFilter === "this-year") {
        const sixtyDaysAgo = new Date();
        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
        if (sixtyDaysAgo < queryStart) {
            return { startDate: sixtyDaysAgo, endDate };
        }
    }

    return { startDate: queryStart, endDate };
};

const mapLeadDocs = (snapshot) => snapshot.docs.map((leadDoc) => ({
    id: leadDoc.id,
    ...leadDoc.data()
}));

const buildLeadsQuery = (businessId, options = {}) => {
    const leadsCol = collection(db, "businesses", businessId, "leads");
    const constraints = [];

    if (options.startDate) {
        constraints.push(where("submittedAt", ">=", Timestamp.fromDate(options.startDate)));
    }
    if (options.endDate) {
        constraints.push(where("submittedAt", "<=", Timestamp.fromDate(options.endDate)));
    }

    constraints.push(orderBy("submittedAt", "desc"));
    return query(leadsCol, ...constraints);
};

export const getLeads = async (businessId, options = {}) => {
    try {
        const snapshot = await getDocs(buildLeadsQuery(businessId, options));
        return mapLeadDocs(snapshot);
    } catch (error) {
        console.error("Error fetching leads:", error);
        throw error;
    }
};

export const subscribeToLeads = (businessId, callback, options = {}) => {
    return onSnapshot(
        buildLeadsQuery(businessId, options),
        (snapshot) => {
            callback(mapLeadDocs(snapshot));
        },
        (error) => {
            console.error("Error subscribing to leads:", error);
            callback([]);
        }
    );
};

export const subscribeToLead = (businessId, leadId, callback) => {
    const leadRef = doc(db, "businesses", businessId, "leads", leadId);

    return onSnapshot(
        leadRef,
        (leadDoc) => {
            callback(leadDoc.exists() ? { id: leadDoc.id, ...leadDoc.data() } : null);
        },
        (error) => {
            console.error("Error subscribing to lead:", error);
            callback(null);
        }
    );
};

// Get form data for a lead
export const getLeadFormData = async (businessId, leadId) => {
    try {
        const leadRef = doc(db, "businesses", businessId, "leads", leadId);
        const leadDoc = await getDoc(leadRef);
        
        if (!leadDoc.exists()) {
            throw new Error("Lead not found");
        }
        
        const leadData = leadDoc.data();
        
        return {
            formData: leadData.formData,
            formInfo: leadData.formInfo,
            submittedAt: leadData.submittedAt
        };
    } catch (error) {
        console.error("Error fetching lead form data:", error);
        throw error;
    }
};

// Update lead status
export const updateLeadStatus = async (businessId, leadId, newStatus, userId, userName) => {
    try {
        const leadRef = doc(db, "businesses", businessId, "leads", leadId);
        
        // Get current lead data
        const leadDoc = await getDoc(leadRef);
        if (!leadDoc.exists()) {
            throw new Error("Lead not found");
        }
        
        const currentLead = leadDoc.data();
        const oldStatus = currentLead.status || "new";
        
        // Update the lead with new status
        await updateDoc(leadRef, {
            status: newStatus,
            lastUpdated: serverTimestamp()
        });
        
        // Add activity log
        const activity = {
            type: "status_change",
            from: oldStatus,
            to: newStatus,
            userId,
            userName: userName || "Unknown User",
            timestamp: new Date(),
            message: `Status changed from ${oldStatus} to ${newStatus}`
        };
        
        // Add activity to the lead document
        const currentActivities = currentLead.activities || [];
        await updateDoc(leadRef, {
            activities: [...currentActivities, activity]
        });
        
    } catch (error) {
        console.error("Error updating lead status:", error);
        throw error;
    }
};

// Assign lead to a team member
export const assignLead = async (businessId, leadId, assignedTo, assignedToName, userId, userName) => {
    try {
        const leadRef = doc(db, "businesses", businessId, "leads", leadId);
        
        // Get current lead data
        const leadDoc = await getDoc(leadRef);
        if (!leadDoc.exists()) {
            throw new Error("Lead not found");
        }
        
        const currentLead = leadDoc.data();
        const oldAssignedTo = currentLead.assignedTo;
        const oldAssignedToName = currentLead.assignedToName;
        
        // Update the lead with new assignment
        await updateDoc(leadRef, {
            assignedTo,
            assignedToName,
            lastUpdated: serverTimestamp()
        });
        
        // Add activity log
        const activity = {
            type: "assignment",
            from: oldAssignedToName || "Unassigned",
            to: assignedToName,
            userId,
            userName: userName || "Unknown User",
            timestamp: new Date(),
            message: `Lead assigned to ${assignedToName}`
        };
        
        // Add activity to the lead document
        const currentActivities = currentLead.activities || [];
        await updateDoc(leadRef, {
            activities: [...currentActivities, activity]
        });
        
        // Send notification to assigned user
        if (assignedTo) {
            const notificationRef = doc(collection(db, "users", assignedTo, "notifications"));
            await setDoc(notificationRef, {
                type: "lead_assigned",
                leadId,
                businessId,
                message: `You have been assigned to lead: ${currentLead.displayName || "Unnamed Lead"}`,
                createdAt: serverTimestamp(),
                read: false,
                link: `/dashboard/leads/${leadId}`,
                leadName: currentLead.displayName || "Unnamed Lead"
            });
        }
        
    } catch (error) {
        console.error("Error assigning lead:", error);
        throw error;
    }
};

// Update lead priority
export const updateLeadPriority = async (businessId, leadId, newPriority, userId, userName) => {
    try {
        const leadRef = doc(db, "businesses", businessId, "leads", leadId);
        
        // Get current lead data
        const leadDoc = await getDoc(leadRef);
        if (!leadDoc.exists()) {
            throw new Error("Lead not found");
        }
        
        const currentLead = leadDoc.data();
        const oldPriority = currentLead.priority || "medium";
        
        // Update the lead with new priority
        await updateDoc(leadRef, {
            priority: newPriority,
            lastUpdated: serverTimestamp()
        });
        
        // Add activity log
        const activity = {
            type: "priority_change",
            from: oldPriority,
            to: newPriority,
            userId,
            userName: userName || "Unknown User",
            timestamp: new Date(),
            message: `Priority changed from ${oldPriority} to ${newPriority}`
        };
        
        // Add activity to the lead document
        const currentActivities = currentLead.activities || [];
        await updateDoc(leadRef, {
            activities: [...currentActivities, activity]
        });
        
    } catch (error) {
        console.error("Error updating lead priority:", error);
        throw error;
    }
};

// Delete activity from lead
export const deleteLeadActivity = async (businessId, leadId, activityIndex, userId, userName) => {
    try {
        const leadRef = doc(db, "businesses", businessId, "leads", leadId);
        
        // Get current lead data
        const leadDoc = await getDoc(leadRef);
        if (!leadDoc.exists()) {
            throw new Error("Lead not found");
        }
        
        const currentLead = leadDoc.data();
        const currentActivities = currentLead.activities || [];
        
        // Check if activity index is valid
        if (activityIndex < 0 || activityIndex >= currentActivities.length) {
            throw new Error("Invalid activity index");
        }
        
        // Remove the activity at the specified index
        const updatedActivities = currentActivities.filter((_, index) => index !== activityIndex);
        
        // Update the lead with the new activities array (no audit trail for deletions)
        await updateDoc(leadRef, {
            activities: updatedActivities,
            lastUpdated: serverTimestamp()
        });
        
    } catch (error) {
        console.error("Error deleting lead activity:", error);
        throw error;
    }
};

// Delete a lead
export const deleteLead = async (businessId, leadId) => {
    try {
        // Delete from nested collection (primary location)
        const leadRef = doc(db, "businesses", businessId, "leads", leadId);
        
        // Verify the document exists before attempting to delete
        const leadDoc = await getDoc(leadRef);
        if (!leadDoc.exists()) {
            throw new Error(`Lead with ID ${leadId} not found in businesses/${businessId}/leads`);
        }
        
        const leadData = leadDoc.data();
        
        // Get business name for Storage path construction
        let businessName = null;
        try {
            const businessRef = doc(db, "businesses", businessId);
            const businessDoc = await getDoc(businessRef);
            if (businessDoc.exists()) {
                businessName = businessDoc.data().businessName;
            }
        } catch (businessError) {
            console.warn("Could not fetch business name:", businessError);
        }
        
        // Delete all files from Firebase Storage
        if (businessName) {
            try {
                const safeBusinessName = businessName.replace(/[^a-zA-Z0-9_-]/g, "_").toLowerCase();
                
                // 1. Delete documents from the documents array
                if (leadData.documents && Array.isArray(leadData.documents)) {
                    console.log(`Deleting ${leadData.documents.length} document(s) from Storage`);
                    const deleteDocumentPromises = leadData.documents
                        .filter(doc => doc.storagePath)
                        .map(documentMetadata => {
                            try {
                                const storageRef = ref(storage, documentMetadata.storagePath);
                                return deleteObject(storageRef);
                            } catch (error) {
                                console.warn(`Error deleting document ${documentMetadata.storagePath}:`, error);
                                return Promise.resolve(); // Continue even if one fails
                            }
                        });
                    await Promise.all(deleteDocumentPromises);
                    console.log(`Successfully deleted documents from Storage`);
                }
                
                // 2. Delete all files in the leads/{leadId} folder
                try {
                    const leadsFolderRef = ref(storage, `${safeBusinessName}/leads/${leadId}`);
                    const folderList = await listAll(leadsFolderRef);
                    
                    if (folderList.items.length > 0) {
                        console.log(`Deleting ${folderList.items.length} file(s) from leads/${leadId} folder`);
                        const deleteFilePromises = folderList.items.map(item => 
                            deleteObject(item).catch(error => {
                                console.warn(`Error deleting file ${item.fullPath}:`, error);
                                return Promise.resolve(); // Continue even if one fails
                            })
                        );
                        await Promise.all(deleteFilePromises);
                        console.log(`Successfully deleted files from leads/${leadId} folder`);
                    }
                } catch (folderError) {
                    console.warn("Error deleting files from leads folder (may not exist):", folderError);
                }
                
                // 3. Extract and delete images from formData (if they're in Lead_Images)
                if (leadData.formData && typeof leadData.formData === 'object') {
                    const imageUrls = Object.values(leadData.formData)
                        .filter(value => typeof value === 'string' && value.startsWith('http'))
                        .filter(url => url.includes('Lead_Images') || url.includes('firebasestorage'));
                    
                    if (imageUrls.length > 0) {
                        console.log(`Found ${imageUrls.length} image URL(s) in formData`);
                        
                        // Helper function to extract storage path from URL
                        const extractStoragePath = (url) => {
                            try {
                                // Try to match the /o/ pattern
                                const urlMatch = url.match(/\/o\/([^?]+)/);
                                if (urlMatch) {
                                    let encodedPath = urlMatch[1];
                                    
                                    // Decode the path - Firebase Storage URLs encode paths
                                    let storagePath = encodedPath;
                                    try {
                                        // Decode once
                                        storagePath = decodeURIComponent(encodedPath);
                                        // If still encoded, decode again
                                        if (storagePath.includes('%')) {
                                            storagePath = decodeURIComponent(storagePath);
                                        }
                                    } catch {
                                        // If decoding fails, replace encoded slashes
                                        storagePath = encodedPath.replace(/%2F/g, '/').replace(/%252F/g, '/');
                                    }
                                    
                                    return storagePath;
                                }
                            } catch (error) {
                                console.warn(`Error extracting path from URL:`, error);
                            }
                            return null;
                        };
                        
                        // Extract storage paths from URLs and delete them
                        const deleteImagePromises = imageUrls.map(async (url) => {
                            try {
                                const storagePath = extractStoragePath(url);
                                
                                if (!storagePath) {
                                    console.warn(`Could not extract path from URL: ${url.substring(0, 100)}...`);
                                    return false;
                                }
                                
                                if (!storagePath.includes('Lead_Images')) {
                                    console.warn(`Skipping URL - path doesn't contain Lead_Images: ${storagePath}`);
                                    return false;
                                }
                                
                                console.log(`Attempting to delete image: ${storagePath}`);
                                const storageRef = ref(storage, storagePath);
                                
                                try {
                                    await deleteObject(storageRef);
                                    console.log(`✅ Successfully deleted image: ${storagePath}`);
                                    return true;
                                } catch (deleteError) {
                                    console.error(`❌ Error deleting image ${storagePath}:`, deleteError);
                                    // Log detailed error info
                                    if (deleteError.code) {
                                        console.error(`Error code: ${deleteError.code}, message: ${deleteError.message}`);
                                    }
                                    // If the file doesn't exist, that's okay - it might have been deleted already
                                    if (deleteError.code === 'storage/object-not-found') {
                                        console.log(`Image ${storagePath} not found (may have been deleted already)`);
                                        return true; // Consider it a success
                                    }
                                    return false;
                                }
                            } catch (error) {
                                console.error(`Error processing image URL:`, error);
                                return false;
                            }
                        });
                        
                        const results = await Promise.all(deleteImagePromises);
                        const successCount = results.filter(r => r === true).length;
                        console.log(`✅ Successfully deleted ${successCount} of ${imageUrls.length} image(s)`);
                    }
                }
            } catch (storageError) {
                // Log but don't throw - continue with lead deletion even if storage deletion fails
                console.warn("Error deleting files from Storage:", storageError);
            }
        }
        
        // First, delete all emails in the emails subcollection
        try {
            const emailsRef = collection(db, "businesses", businessId, "leads", leadId, "emails");
            const emailsSnapshot = await getDocs(emailsRef);
            
            if (!emailsSnapshot.empty) {
                console.log(`Deleting ${emailsSnapshot.docs.length} email(s) from lead ${leadId}`);
                const deleteEmailPromises = emailsSnapshot.docs.map(emailDoc => 
                    deleteDoc(doc(db, "businesses", businessId, "leads", leadId, "emails", emailDoc.id))
                );
                await Promise.all(deleteEmailPromises);
                console.log(`Successfully deleted ${emailsSnapshot.docs.length} email(s)`);
            }
        } catch (emailDeleteError) {
            // Log but don't throw - continue with lead deletion even if email deletion fails
            console.warn("Error deleting emails subcollection:", emailDeleteError);
        }
        
        console.log(`Deleting lead ${leadId} from businesses/${businessId}/leads`);
        await deleteDoc(leadRef);
        console.log(`Successfully deleted lead ${leadId} from nested collection`);
        
        // Also delete from top-level leads collection if it exists (for backward compatibility)
        // This handles leads created by the Cloud Function which creates in both locations
        try {
            const topLevelLeadRef = doc(db, "leads", leadId);
            const topLevelLeadDoc = await getDoc(topLevelLeadRef);
            if (topLevelLeadDoc.exists()) {
                console.log(`Deleting lead ${leadId} from top-level leads collection`);
                await deleteDoc(topLevelLeadRef);
                console.log(`Successfully deleted lead ${leadId} from top-level collection`);
            }
        } catch (topLevelError) {
            // If top-level deletion fails, log but don't throw (it might not exist)
            console.warn("Could not delete from top-level leads collection (may not exist):", topLevelError);
        }
    } catch (error) {
        console.error("Error deleting lead:", error);
        console.error("Error details:", {
            code: error.code,
            message: error.message,
            businessId,
            leadId
        });
        throw error;
    }
};
